import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { 
  Plus, 
  FileText, 
  Edit, 
  Trash2, 
  Loader2,
  Search,
  Tag,
  Circle
} from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const getStatusBadge = (status) => {
  switch (status) {
    case "registered":
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 border border-emerald-200 text-xs font-mono uppercase">Registered</span>;
    case "examination":
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 border border-blue-200 text-xs font-mono uppercase">Examination</span>;
    case "published":
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 border border-purple-200 text-xs font-mono uppercase">Published</span>;
    case "opposed":
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 border border-orange-200 text-xs font-mono uppercase">Opposed</span>;
    case "dead":
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-500 border border-slate-200 text-xs font-mono uppercase">Dead</span>;
    default:
      return <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs font-mono uppercase">{status}</span>;
  }
};

export const Trademarks = () => {
  const navigate = useNavigate();
  const [trademarks, setTrademarks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const fetchData = async () => {
    try {
      const [trademarksRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/trademarks`),
        axios.get(`${API}/categories`)
      ]);
      setTrademarks(trademarksRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load trademarks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/trademarks/${deleteId}`);
      toast.success("Trademark deleted successfully");
      setTrademarks(trademarks.filter(t => t.id !== deleteId));
    } catch (error) {
      console.error("Error deleting trademark:", error);
      toast.error("Failed to delete trademark");
    } finally {
      setDeleteId(null);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.id === categoryId);
    return category ? category.name : categoryId;
  };

  const filteredTrademarks = trademarks.filter(trademark =>
    trademark.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trademark.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    trademark.owner?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="trademarks-loading">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="trademarks-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-heading font-black text-slate-900 tracking-tight">
            My Trademarks
          </h1>
          <p className="text-slate-500 mt-1">Manage your protected trademarks</p>
        </div>
        <Button
          onClick={() => navigate('/trademarks/new')}
          className="btn-primary flex items-center gap-2"
          data-testid="add-trademark-btn"
        >
          <Plus className="w-4 h-4" />
          ADD TRADEMARK
        </Button>
      </div>

      {/* Search */}
      <div className="swiss-card mb-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search trademarks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-swiss pl-12"
            data-testid="search-trademarks-input"
          />
        </div>
      </div>

      {/* Trademarks List */}
      {filteredTrademarks.length === 0 ? (
        <div className="swiss-card text-center py-12" data-testid="no-trademarks-message">
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">
            {searchTerm ? "No trademarks match your search" : "No trademarks added yet"}
          </p>
          {!searchTerm && (
            <p className="text-sm text-slate-400 mt-1">
              Add your first trademark to start monitoring
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4 stagger-children" data-testid="trademarks-list">
          {filteredTrademarks.map((trademark, index) => (
            <motion.div
              key={trademark.id}
              className="swiss-card hover:shadow-md transition-shadow"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`trademark-${trademark.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-heading font-bold text-slate-900">
                      {trademark.name}
                    </h3>
                    {getStatusBadge(trademark.trademark_status)}
                  </div>
                  {trademark.description && (
                    <p className="text-slate-500 text-sm mb-3">{trademark.description}</p>
                  )}
                  {trademark.owner && (
                    <p className="text-sm text-slate-600 mb-2">
                      <span className="font-semibold">Owner:</span> {trademark.owner}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    {trademark.categories?.map(catId => (
                      <span 
                        key={catId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 text-xs font-mono"
                      >
                        <Tag className="w-3 h-3" />
                        {getCategoryName(catId)}
                      </span>
                    ))}
                  </div>
                  {trademark.registration_number && (
                    <p className="text-xs text-slate-400 mt-3 font-mono">
                      Reg: {trademark.registration_number}
                      {trademark.registration_date && ` | ${trademark.registration_date}`}
                    </p>
                  )}
                  {/* Watching Service & Expiry Info */}
                  <div className="flex flex-wrap gap-4 mt-3 text-xs">
                    {trademark.watching_search_registered !== undefined && (
                      <span className={`inline-flex items-center gap-1 px-2 py-1 ${trademark.watching_search_registered ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-50 text-slate-500'}`}>
                        <Circle className={`w-2 h-2 ${trademark.watching_search_registered ? 'fill-emerald-500 text-emerald-500' : 'fill-slate-300 text-slate-300'}`} />
                        Watching: {trademark.watching_search_registered ? 'Yes' : 'No'}
                      </span>
                    )}
                    {trademark.watching_search_expiry_date && (
                      <span className="text-slate-500">
                        Watch Expiry: <span className="font-mono">{trademark.watching_search_expiry_date}</span>
                      </span>
                    )}
                    {trademark.registration_expiry_date && (
                      <span className="text-slate-500">
                        Reg Expiry: <span className="font-mono">{trademark.registration_expiry_date}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/trademarks/edit/${trademark.id}`)}
                    className="btn-secondary"
                    data-testid={`edit-trademark-${trademark.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeleteId(trademark.id)}
                    className="btn-secondary text-red-600 hover:bg-red-50"
                    data-testid={`delete-trademark-${trademark.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Trademark</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this trademark? This will also remove all associated alerts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
